import alertSignal from 'signals/Alert.signal';
import { edoUsersSignal } from 'signals/EdoUsers.signal';
import loaderSignal from 'signals/Loader.signal';
import api from 'api/api';
import userAccountSignal from 'signals/UserAccount.signal';
import history from '../../../../history';

export const handleEdoSettingsUserDetailsChange = (e) => {
  const { form } = edoUsersSignal.value;
  return edoUsersSignal.update({
    form: {
      ...form,
      [e.target.name]: e.target.value,
    },
  });
};

export const setEdoSettingsUserDetailUser = (selectedUser) => {
  const form = {
    firstName: selectedUser.firstName,
    lastName: selectedUser.lastName,
    email: selectedUser.email,
    phoneNumber: selectedUser.phoneNumber,
    role: selectedUser.role,
    edoUserProgramAccesses: (selectedUser.edoUserProgramAccesses || []).reduce(
      (acc, eupa) => ({
        ...acc,
        [eupa.fundingProgramId]: eupa.role,
      }),
      {}
    ),
    edoUserLenderAccesses: (selectedUser.edoUserLenderAccesses || []).map((eula) => eula.lenderId),
  };
  return edoUsersSignal.update({ selectedUser, form });
};

export const fetchAndSetUsersData = async (isHideLoader) => {
  loaderSignal.update({ isContentLoading: !isHideLoader });
  try {
    const [users, fundingPrograms, lenders] = await Promise.all([
      api.get({
        path: '/users',
        options: {
          where: xFormAppliedFiltersToWhereClause(),
          include: {
            account: true,
            edoUserProgramAccesses: true,
            edoUserLenderAccesses: true,
          },
          orderBy: xFormSortToOrderByClause(),
        },
      }),
      api.get({
        path: '/fundingPrograms',
        options: {
          where: {
            edoId: userAccountSignal.value.userData?.account.edoId,
          },
          include: {
            referenceProgram: true,
          },
        },
      }),
      api.get({
        path: '/lenders',
        options: {
          // Fetch all lenders - admin can associate any lender to a user
          orderBy: {
            name: 'asc',
          },
        },
      }),
    ]);
    return edoUsersSignal.update({ users, fundingPrograms, lenders });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const handleEdoSettingsUserDetailsUpdateUser = async () => {
  loaderSignal.update({ isContentLoading: true, message: 'Updating user details...' });
  const { firstName, lastName, phoneNumber, role } = edoUsersSignal.value.form;

  const { selectedUser } = edoUsersSignal.value;

  try {
    await api.patch({
      path: '/users',
      body: {
        where: { id: selectedUser.id },
        data: {
          firstName,
          lastName,
          phoneNumber,
          role,
        },
      },
    });
    await fetchAndSetUsersData();
    // Refresh selected user if one is set
    if (edoUsersSignal.value.selectedUser?.id) {
      const updatedUser = edoUsersSignal.value.users.find(
        (u) => u.id === edoUsersSignal.value.selectedUser.id
      );
      if (updatedUser) {
        setEdoSettingsUserDetailUser(updatedUser);
      }
    }
    return alertSignal.update({
      variant: 'success',
      type: 'notification',
      message: 'Successfully updated user',
    });
  } catch (error) {
    return alertSignal.update({
      type: 'alert',
      error,
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const deleteUser = async (user) => {
  loaderSignal.update({ isContentLoading: true });
  try {
    await api.delete({ path: '/users', body: { user } });
    await fetchAndSetUsersData();
  } catch (error) {
    alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const handleProgramAccessUpdate = async (e, payload) => {
  try {
    loaderSignal.update({ isContentLoading: true, message: 'Updating program access...' });

    const { programAccess, fundingProgram, role } = payload;
    const { form } = edoUsersSignal.value;
    const { edoUserProgramAccesses } = form;
    edoUserProgramAccesses[fundingProgram.id] = role;
    edoUsersSignal.update({ form: { ...form, edoUserProgramAccesses } });
    const userId = edoUsersSignal.value.selectedUser.id;
    const fundingProgramId = fundingProgram.id;
    const path = '/edo/user/program/access';

    // If role is empty, remove program access
    if (!role || role === '') {
      if (programAccess?.id) {
        await api.delete({ path, body: { where: { id: programAccess.id } } });
      }
    } else {
      await api.put({
        path,
        body: {
          where: {
            userId_fundingProgramId: { userId, fundingProgramId },
          },
          create: {
            edo: {
              connect: {
                id: userAccountSignal.value.userData.account?.edo?.id,
              },
            },
            user: {
              connect: {
                id: userId,
              },
            },
            fundingProgram: {
              connect: {
                id: fundingProgramId,
              },
            },
            role,
          },
          update: {
            role,
          },
        },
      });
    }
    await fetchAndSetUsersData();
    const updatedUser = edoUsersSignal.value.users.find(
      (u) => edoUsersSignal.value.selectedUser?.id === u.id
    );
    if (updatedUser) {
      setEdoSettingsUserDetailUser(updatedUser);
    }
    return alertSignal.update({
      variant: 'success',
      type: 'notification',
      message: 'Successfully updated program access',
    });
  } catch (error) {
    return alertSignal.update({
      type: 'alert',
      error,
      message: 'Unable to update program access',
    });
  } finally {
    loaderSignal.reset();
  }
};

const xFormSortToOrderByClause = () => {
  const sort = edoUsersSignal.value.sort;
  if (!sort) {
    return undefined;
  }

  if (sort.key === 'name') {
    return [
      {
        lastName: sort.dir,
      },
    ];
  } else {
    return [{ [sort.key]: sort.dir }];
  }
};

export const handleUserSortChange = (sort) =>
  edoUsersSignal.update({
    sort,
  });

export const handleEdoManageUsersAppliedFiltersChange = (appliedFilters) =>
  edoUsersSignal.update({
    appliedFilters,
  });

export const handleEdoManageUsersFilterChange = (key, value) => {
  const dropdownFilters = edoUsersSignal.value.dropdownFilters;
  return edoUsersSignal.update({
    dropdownFilters: { ...dropdownFilters, [key]: value },
  });
};

const xFormAppliedFiltersToWhereClause = () => {
  const where = {
    accountId: userAccountSignal.value.userData?.accountId,
  };

  const params =
    Object.fromEntries(new URLSearchParams(history.location.search)) || {};

  if (params.firstName) {
    where.firstName = { contains: params.firstName, mode: 'insensitive' };
  }

  if (params.lastName) {
    where.lastName = { contains: params.lastName, mode: 'insensitive' };
  }

  if (params.email) {
    where.email = { contains: params.email, mode: 'insensitive' };
  }

  if (params.id) {
    where.id = { in: [Number(params.id)] };
  }

  if (params.role) {
    where.role = params.role;
  }

  if (params.status) {
    where.status = params.status;
  }

  return where;
};

export const resetEdoManageUsersFilterInputs = () =>
  edoUsersSignal.update({ dropdownFilters: {} });

export const handleLenderAccessUpdate = async (payload) => {
  try {
    loaderSignal.update({ isContentLoading: true, message: 'Updating lender access...' });

    const { lender, lenderAccess, isSelected } = payload;
    const { form } = edoUsersSignal.value;
    const edoUserLenderAccesses = form.edoUserLenderAccesses || [];
    const userId = edoUsersSignal.value.selectedUser.id;
    const lenderId = lender.id;
    const path = '/edo/user/lender/access';

    // If already selected, remove lender access
    if (isSelected) {
      if (lenderAccess?.id) {
        await api.delete({ path, body: { where: { id: lenderAccess.id } } });
      }
      // Update form to remove lender ID
      const updatedLenderAccesses = edoUserLenderAccesses.filter(id => id !== lenderId);
      edoUsersSignal.update({ form: { ...form, edoUserLenderAccesses: updatedLenderAccesses } });
    } else {
      // Add lender access
      await api.put({
        path,
        body: {
          where: {
            userId_lenderId: { userId, lenderId },
          },
          create: {
            edo: {
              connect: {
                id: userAccountSignal.value.userData.account?.edo?.id,
              },
            },
            user: {
              connect: {
                id: userId,
              },
            },
            lender: {
              connect: {
                id: lenderId,
              },
            },
          },
          update: {},
        },
      });
      // Update form to add lender ID
      const updatedLenderAccesses = [...edoUserLenderAccesses, lenderId];
      edoUsersSignal.update({ form: { ...form, edoUserLenderAccesses: updatedLenderAccesses } });
    }
    await fetchAndSetUsersData();
    const updatedUser = edoUsersSignal.value.users.find(
      (u) => edoUsersSignal.value.selectedUser?.id === u.id
    );
    if (updatedUser) {
      setEdoSettingsUserDetailUser(updatedUser);
    }
    return alertSignal.update({
      variant: 'success',
      type: 'notification',
      message: 'Successfully updated lender access',
    });
  } catch (error) {
    return alertSignal.update({
      type: 'alert',
      error,
      message: 'Unable to update lender access',
    });
  } finally {
    loaderSignal.reset();
  }
};

export const handleSelectAllLenders = async (selectAll, lenders, lenderAccesses) => {
  try {
    loaderSignal.update({ isContentLoading: true, message: selectAll ? 'Granting access to all lenders...' : 'Removing access from all lenders...' });

    const { form } = edoUsersSignal.value;
    const userId = edoUsersSignal.value.selectedUser.id;
    const edoId = userAccountSignal.value.userData.account?.edo?.id;
    const path = '/edo/user/lender/access';

    if (selectAll) {
      // Add access to all lenders that don't already have access
      const currentLenderIds = (form.edoUserLenderAccesses || []).map(id => Number(id));
      const lendersToAdd = lenders.filter(lender => !currentLenderIds.includes(lender.id));

      // Batch add all lenders
      await Promise.all(
        lendersToAdd.map((lender) =>
          api.put({
            path,
            body: {
              where: {
                userId_lenderId: { userId, lenderId: lender.id },
              },
              create: {
                edo: {
                  connect: {
                    id: edoId,
                  },
                },
                user: {
                  connect: {
                    id: userId,
                  },
                },
                lender: {
                  connect: {
                    id: lender.id,
                  },
                },
              },
              update: {},
            },
          })
        )
      );

      // Update form with all lender IDs
      const allLenderIds = lenders.map(l => l.id);
      edoUsersSignal.update({ form: { ...form, edoUserLenderAccesses: allLenderIds } });
    } else {
      // Remove access from all lenders
      const lenderAccessIds = lenderAccesses.map(la => la.id);
      
      // Batch delete all lender accesses
      await Promise.all(
        lenderAccessIds.map((id) =>
          api.delete({ path, body: { where: { id } } })
        )
      );

      // Update form to remove all lender IDs
      edoUsersSignal.update({ form: { ...form, edoUserLenderAccesses: [] } });
    }

    await fetchAndSetUsersData();
    const updatedUser = edoUsersSignal.value.users.find(
      (u) => edoUsersSignal.value.selectedUser?.id === u.id
    );
    if (updatedUser) {
      setEdoSettingsUserDetailUser(updatedUser);
    }
    return alertSignal.update({
      variant: 'success',
      type: 'notification',
      message: selectAll ? 'Successfully granted access to all lenders' : 'Successfully removed access from all lenders',
    });
  } catch (error) {
    return alertSignal.update({
      type: 'alert',
      error,
      message: 'Unable to update lender access',
    });
  } finally {
    loaderSignal.reset();
  }
};
